import React, { useMemo, useState, useCallback } from 'react';
import { BarChart3, ChevronDown, ChevronRight, Layers, Music2, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * EraChart — Decade/year breakdown of a station's airplay.
 *
 * Renders nested horizontal bars: a bar per decade with the years inside it
 * shown as smaller sub-bars (collapsible). Two modes via toggle:
 *  - "plays"   → volume of airplay (count of total plays)
 *  - "variety" → catalog depth (count of unique songs)
 *
 * Clicking on a decade or year fires `onEraSelect({type, value, label})`
 * so the parent page can switch back to the main song table filtered by
 * that period.
 *
 * This component does no fetching of its own — it works off the
 * `songs` array already loaded on the page (each item must have
 * `release_year` and `plays`).
 */

// Decade → tailwind color mapping (kept calm/distinct, not heat-coded
// because the user explicitly opted out of national-average comparison).
const DECADE_PALETTE = {
  '2020s': { bar: 'bg-emerald-500',  bg: 'bg-emerald-500/10',  border: 'border-emerald-500/30',  text: 'text-emerald-300' },
  '2010s': { bar: 'bg-blue-500',     bg: 'bg-blue-500/10',     border: 'border-blue-500/30',     text: 'text-blue-300' },
  '2000s': { bar: 'bg-violet-500',   bg: 'bg-violet-500/10',   border: 'border-violet-500/30',   text: 'text-violet-300' },
  '1990s': { bar: 'bg-amber-500',    bg: 'bg-amber-500/10',    border: 'border-amber-500/30',    text: 'text-amber-300' },
  '1980s': { bar: 'bg-rose-500',     bg: 'bg-rose-500/10',     border: 'border-rose-500/30',     text: 'text-rose-300' },
  '1970s': { bar: 'bg-pink-500',     bg: 'bg-pink-500/10',     border: 'border-pink-500/30',     text: 'text-pink-300' },
  '1960s': { bar: 'bg-orange-500',   bg: 'bg-orange-500/10',   border: 'border-orange-500/30',   text: 'text-orange-300' },
  'earlier': { bar: 'bg-slate-500',  bg: 'bg-slate-500/10',    border: 'border-slate-500/30',    text: 'text-slate-300' },
  'unknown': { bar: 'bg-gray-500',   bg: 'bg-gray-500/10',     border: 'border-gray-500/30',     text: 'text-gray-300' },
};

const DECADE_ORDER = ['2020s', '2010s', '2000s', '1990s', '1980s', '1970s', '1960s', 'earlier', 'unknown'];

const decadeKeyFromYear = (year) => {
  if (!year || year === 'unknown' || isNaN(year)) return 'unknown';
  const y = parseInt(year, 10);
  if (y < 1960) return 'earlier';
  if (y >= 2020) return '2020s';
  if (y >= 2010) return '2010s';
  if (y >= 2000) return '2000s';
  if (y >= 1990) return '1990s';
  if (y >= 1980) return '1980s';
  if (y >= 1970) return '1970s';
  if (y >= 1960) return '1960s';
  return 'earlier';
};

const decadeLabel = (decade, t) => {
  if (decade === 'unknown') return t ? t('eraChartUnknownYear') : 'Άγνωστο έτος';
  if (decade === 'earlier') return 'Pre-1960';
  return decade;
};

const EraChart = ({ songs = [], onEraSelect }) => {
  const { t } = useLanguage();
  // Mode toggle: 'plays' (volume) vs 'variety' (unique songs count)
  const [mode, setMode] = useState('plays');
  const [expandedDecades, setExpandedDecades] = useState(new Set(['2020s', '2010s']));

  const toggleDecade = useCallback((decade) => {
    setExpandedDecades(prev => {
      const next = new Set(prev);
      if (next.has(decade)) next.delete(decade);
      else next.add(decade);
      return next;
    });
  }, []);

  // Aggregate songs into decade → year buckets. Both metrics are computed
  // in a single pass so the toggle is instant (no re-aggregation).
  const aggregated = useMemo(() => {
    const buckets = {};
    let grandTotalPlays = 0;
    let grandTotalUnique = 0;

    for (const song of songs) {
      const yr = song.release_year;
      const decade = decadeKeyFromYear(yr);
      const yearKey = (yr && !isNaN(yr)) ? String(yr) : 'unknown';
      const plays = Number(song.plays) || 0;

      if (!buckets[decade]) {
        buckets[decade] = { plays: 0, unique: 0, years: {} };
      }
      buckets[decade].plays += plays;
      buckets[decade].unique += 1;
      grandTotalPlays += plays;
      grandTotalUnique += 1;

      const yb = buckets[decade].years[yearKey] || { plays: 0, unique: 0 };
      yb.plays += plays;
      yb.unique += 1;
      buckets[decade].years[yearKey] = yb;
    }

    // Convert to ordered array
    const decades = DECADE_ORDER
      .filter(d => buckets[d])
      .map(d => {
        const data = buckets[d];
        const years = Object.entries(data.years)
          .map(([year, v]) => ({
            year,
            plays: v.plays,
            unique: v.unique,
            playsPct: grandTotalPlays > 0 ? (v.plays / grandTotalPlays) * 100 : 0,
            uniquePct: grandTotalUnique > 0 ? (v.unique / grandTotalUnique) * 100 : 0,
          }))
          // Numeric desc sort, but push 'unknown' to bottom
          .sort((a, b) => {
            if (a.year === 'unknown') return 1;
            if (b.year === 'unknown') return -1;
            return parseInt(b.year, 10) - parseInt(a.year, 10);
          });

        return {
          key: d,
          label: decadeLabel(d, t),
          plays: data.plays,
          unique: data.unique,
          playsPct: grandTotalPlays > 0 ? (data.plays / grandTotalPlays) * 100 : 0,
          uniquePct: grandTotalUnique > 0 ? (data.unique / grandTotalUnique) * 100 : 0,
          years,
        };
      })
      // Sort by current metric (descending), keep 'unknown' last
      .sort((a, b) => {
        if (a.key === 'unknown') return 1;
        if (b.key === 'unknown') return -1;
        const av = mode === 'plays' ? a.plays : a.unique;
        const bv = mode === 'plays' ? b.plays : b.unique;
        return bv - av;
      });

    return { decades, totalPlays: grandTotalPlays, totalUnique: grandTotalUnique };
  }, [songs, mode]);

  const handleEraClick = useCallback((type, value, label, e) => {
    e.stopPropagation();
    if (typeof onEraSelect === 'function') {
      onEraSelect({ type, value, label });
    }
  }, [onEraSelect]);

  if (!songs || songs.length === 0) {
    return (
      <div className="text-center py-12 text-white/50" data-testid="era-chart-empty">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>Δεν υπάρχουν δεδομένα για ανάλυση εποχής. Επίλεξε σταθμό και περίοδο πρώτα.</p>
      </div>
    );
  }

  const metricLabel = mode === 'plays' ? t('eraChartPlays') : t('eraChartUnique');
  const metricLabelShort = metricLabel;
  const footerPrefix = mode === 'plays' ? t('eraChartFooterPlays') : t('eraChartFooterUnique');

  return (
    <div className="space-y-4" data-testid="era-chart-root">
      {/* Header + Mode toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-amber-400" />
            {t('eraChartTitle')}
          </h3>
          <p className="text-xs text-white/50 mt-0.5">
            {t('eraChartSubtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Totals badge */}
          <div className="hidden sm:flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-white/60">
              <Music2 className="w-3.5 h-3.5" />
              <span className="font-medium text-white">{aggregated.totalPlays.toLocaleString('el-GR')}</span> {t('eraChartPlays')}
            </span>
            <span className="flex items-center gap-1 text-white/60">
              <span className="font-medium text-white">{aggregated.totalUnique.toLocaleString('el-GR')}</span> {t('eraChartUnique')}
            </span>
          </div>

          {/* Mode toggle */}
          <div
            className="flex items-center gap-0.5 bg-white/10 rounded-lg p-0.5 border border-white/10"
            data-testid="era-chart-mode-toggle"
          >
            <button
              onClick={() => setMode('plays')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                mode === 'plays'
                  ? 'bg-amber-500 text-black shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
              data-testid="era-chart-mode-plays"
            >
              {t('eraChartTogglePlays')}
            </button>
            <button
              onClick={() => setMode('variety')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                mode === 'variety'
                  ? 'bg-amber-500 text-black shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
              data-testid="era-chart-mode-variety"
            >
              {t('eraChartToggleVariety')}
            </button>
          </div>
        </div>
      </div>

      {/* Decade list */}
      <div className="space-y-3" data-testid="era-chart-decades">
        {aggregated.decades.map(decade => {
          const palette = DECADE_PALETTE[decade.key] || DECADE_PALETTE.unknown;
          const decadeValue = mode === 'plays' ? decade.plays : decade.unique;
          const decadePct = mode === 'plays' ? decade.playsPct : decade.uniquePct;
          const isExpanded = expandedDecades.has(decade.key);
          const canExpand = decade.years.length > 1; // single-year decades aren't worth expanding

          return (
            <div
              key={decade.key}
              className={`rounded-xl border ${palette.border} ${palette.bg} overflow-hidden`}
              data-testid={`era-chart-decade-${decade.key}`}
            >
              {/* Decade header bar */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {canExpand ? (
                      <button
                        onClick={() => toggleDecade(decade.key)}
                        className="text-white/60 hover:text-white transition-colors"
                        data-testid={`era-chart-toggle-${decade.key}`}
                        aria-label={isExpanded ? t('i18nB3_collapse') : t('i18nB3_expand')}
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    ) : (
                      <span className="w-4" />
                    )}
                    <button
                      onClick={(e) => handleEraClick('decade', decade.key, decade.label, e)}
                      className={`font-semibold ${palette.text} hover:underline truncate`}
                      title={`Φιλτράρισε «Σύνολο» για ${decade.label}`}
                      data-testid={`era-chart-decade-link-${decade.key}`}
                    >
                      {decade.label}
                    </button>
                    <span className="text-xs text-white/40 hidden sm:inline">
                      ({decade.years.length} {decade.years.length === 1 ? t('eraChartYear') : t('eraChartYears')})
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm shrink-0">
                    <span className="font-semibold text-white tabular-nums">
                      {decadeValue.toLocaleString('el-GR')}
                    </span>
                    <span className="text-xs text-white/40 hidden sm:inline">{metricLabelShort}</span>
                    <span className={`font-medium ${palette.text} tabular-nums w-12 text-right`}>
                      {decadePct.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* Decade horizontal bar */}
                <button
                  onClick={(e) => handleEraClick('decade', decade.key, decade.label, e)}
                  className="w-full h-3 bg-white/5 rounded-full overflow-hidden hover:ring-2 hover:ring-white/20 transition-all"
                  title={`Φιλτράρισε «Σύνολο» για ${decade.label}`}
                  data-testid={`era-chart-decade-bar-${decade.key}`}
                >
                  <div
                    className={`h-full ${palette.bar} transition-all duration-500 ease-out`}
                    style={{ width: `${Math.max(decadePct, 1)}%` }}
                  />
                </button>
              </div>

              {/* Year sub-bars (collapsible) */}
              {isExpanded && canExpand && (
                <div
                  className="px-4 pb-4 pt-1 space-y-1.5 bg-black/10 border-t border-white/5"
                  data-testid={`era-chart-years-${decade.key}`}
                >
                  {decade.years.map(yr => {
                    const yrValue = mode === 'plays' ? yr.plays : yr.unique;
                    const yrPct = mode === 'plays' ? yr.playsPct : yr.uniquePct;
                    // Local relative width: scale within the decade's max so a 0.5%
                    // year still shows a visible sliver.
                    const decadeMaxPct = mode === 'plays' ? decade.playsPct : decade.uniquePct;
                    const relWidth = decadeMaxPct > 0 ? (yrPct / decadeMaxPct) * 100 : 0;
                    const isFilterable = yr.year !== 'unknown';
                    return (
                      <div
                        key={yr.year}
                        className="flex items-center gap-2 text-xs"
                        data-testid={`era-chart-year-row-${yr.year}`}
                      >
                        <button
                          onClick={(e) => isFilterable && handleEraClick('year', parseInt(yr.year, 10), yr.year, e)}
                          disabled={!isFilterable}
                          className={`w-12 text-right tabular-nums shrink-0 ${
                            isFilterable
                              ? `${palette.text} hover:underline cursor-pointer`
                              : 'text-white/40 cursor-default'
                          }`}
                          title={isFilterable ? `Φιλτράρισε «Σύνολο» για ${yr.year}` : ''}
                          data-testid={`era-chart-year-link-${yr.year}`}
                        >
                          {yr.year === 'unknown' ? '???' : yr.year}
                        </button>
                        <button
                          onClick={(e) => isFilterable && handleEraClick('year', parseInt(yr.year, 10), yr.year, e)}
                          disabled={!isFilterable}
                          className={`flex-1 h-2 bg-white/5 rounded-full overflow-hidden ${
                            isFilterable ? 'hover:ring-1 hover:ring-white/20 cursor-pointer' : 'cursor-default'
                          }`}
                          title={isFilterable ? `Φιλτράρισε «Σύνολο» για ${yr.year}` : ''}
                        >
                          <div
                            className={`h-full ${palette.bar} opacity-70 transition-all duration-500 ease-out`}
                            style={{ width: `${Math.max(relWidth, 2)}%` }}
                          />
                        </button>
                        <span className="text-white/70 tabular-nums w-10 text-right shrink-0">
                          {yrValue.toLocaleString('el-GR')}
                        </span>
                        <span className="text-white/40 tabular-nums w-12 text-right shrink-0">
                          {yrPct.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="text-xs text-white/40 flex items-center gap-2 pt-2">
        <Filter className="w-3 h-3" />
        <span>
          {footerPrefix} <strong className="text-white/70">
            {(mode === 'plays' ? aggregated.totalPlays : aggregated.totalUnique).toLocaleString('el-GR')}
          </strong>
        </span>
      </div>
    </div>
  );
};

export default EraChart;
